<template lang="pug">
div
  div(@click='modal_open')
    slot(name='activator')
  v-dialog(v-model='value', persistent, max-width='650')
    v-snackbar(:value='message', :key='errorMsgKey', color='red darken-2')
      span Error while creating transaction: {{ message }}
    v-card
      title-bar(dark) Create TransactionDesk Transaction
      v-card-text
        v-form(ref='form')
          v-layout.px-1(column, align-center, justify-space-between)
            .pa-2
              v-img(:src='tdLogo', width='350', height='71')
            v-flex.mt-4.form-container(xs3)
              p.mb-1.font-weight-bold.text-sm-left
                | Transaction details
              v-layout(row, justify-space-between)
                v-text-field.form-input(
                  label='Transaction name*',
                  placeholder=' ',
                  v-model='transaction.name',
                  :rules='[rules.required]'
                )
                v-select.form-input(
                  label='Transaction type*',
                  v-model='transaction.TypeId',
                  :items='transactionTypes',
                  :rules='[rules.required]',
                  item-text='name',
                  item-value='id'
                )
                  template(v-if="!hasFinishedLoadingTransactionTypes" slot="append-item")
                    v-list-tile
                      v-list-tile-content
                        a(color="primary" @click="fetchAdditionalTransactionTypes()")
                          | Load more ...
            v-flex.mt-3.form-container(xs3)
              p.mb-2.font-weight-bold.text-sm-left
                | TransactionDesk contact
              v-layout.mb-3(row, justify-space-between)
                v-text-field.mt-0.form-input(
                  label='First name*',
                  v-model='transactionContact.firstName',
                  :rules='[rules.required]',
                  :readonly='readOnlyFields.firstName'
                )
                v-text-field.mt-0.form-input(
                  label='Last name*',
                  v-model='transactionContact.lastName',
                  :rules='[rules.required]',
                  :readonly='readOnlyFields.lastName'
                )
              v-layout.mt-3(row, justify-space-between)
                v-select.form-input(
                  label='Contact Type*',
                  v-model='transactionContact.typeId',
                  :items='contactTypes',
                  :rules='[rules.required]',
                  item-text='name',
                  item-value='id'
                )
                  template(v-if="!hasFinishedLoadingContactTypes" slot="append-item")
                    v-list-tile
                      v-list-tile-content
                        a(color="primary" @click="fetchAdditionalContactTypes()")
                          | Load more ...
                v-text-field.mt-0.form-input(
                  label='Phone',
                  v-model='transactionContact.phone',
                  :rules='[rules.phone]',
                  :readonly='readOnlyFields.phone'
                )
              v-layout.mt-3(row, justify-space-between)
                v-text-field.mt-0.form-input(
                  label='Email',
                  v-model='transactionContact.email',
                  :rules='[rules.email]',
                  :readonly='readOnlyFields.email'
                )
              p.ma-0.pt-3.text-sm-left.caption.grey--text * Field is required
              p.pa-0.ma-0.mt-3.text-sm-left.red-darken-2--text.bottom-text Additional transaction fields can be filled out in TransactionDesk after you clicked create and go to TransactionDesk
      v-card-actions
        v-spacer
        v-btn(flat, @click='cancel()') Cancel
        v-btn.primary(@click='createTransactionDeskTransaction()', :disabled='isLoading') Create and go to TransactionDesk
</template>

<script>
import Modal from '@/Core/mixins/modal';
import TitleBar from '@/Core/components/TitleBar';
import { mapState, mapActions, mapGetters } from 'vuex';
import { PHONE_REGEX, EMAIL_REGEX } from '@/Core/config/regex';
import { INTEGRATION_STATUS } from './constants';

export default {
  name: 'CreateTransactionDesk',
  components: {
    TitleBar,
  },
  mixins: [Modal],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tdLogo: '/static/img/lw-td-logo.png',
      transaction: {
        name: '',
        TypeId: '',
      },
      transactionContact: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        typeId: '',
      },
      readOnlyFields: {
        firstName: false,
        lastName: false,
        phone: false,
        email: false,
      },
      rules: {
        required: value => {
          return !!value || 'This field is required';
        },
        phone: value => {
          return !value || PHONE_REGEX.test(value) || 'Please enter a valid phone number';
        },
        email: value => {
          return !value || EMAIL_REGEX.test(value) || 'Invalid email address';
        },
      },
    };
  },
  async created() {
    await this.getIntegrationStatus();
    this.setDefaultFormValues();

    if (this.integrationStatus === INTEGRATION_STATUS.DISCONNECTED) return;

    await this.fetchTransactionTypes(0);
    await this.fetchContactTypes(0);
  },
  watch: {
    transactionData(newValue, oldValue) {
      if (newValue === oldValue) return;
      if (!this.hasError) {
        this.cancel();
        this.openTransactionDeskURL();
        this.refreshContactEventTimeline();
      }
    },
    currentContact() {
      this.setDefaultFormValues();
    },
    integrationStatus(newValue, oldValue) {
      if (newValue === oldValue) return;
      if (newValue === INTEGRATION_STATUS.CONNECTED) {
        this.fetchTransactionTypes(0);
        this.fetchContactTypes(0);
      }
    },
  },
  computed: {
    ...mapGetters('contact', ['currentContact']),
    ...mapState('transactionDesk', [
      'transactionData',
      'hasError',
      'message',
      'errorMsgKey',
      'isLoading',
      'contactTypes',
      'transactionTypes',
      'integrationStatus',
      'hasFinishedLoadingContactTypes',
      'hasFinishedLoadingTransactionTypes',
    ]),
  },
  methods: {
    ...mapActions('transactionDesk', [
      'createTransaction',
      'fetchTransactionTypes',
      'getIntegrationStatus',
      'fetchContactTypes',
    ]),
    cancel() {
      this.$emit('close');
    },
    setIfShouldBeReadOnly() {
      this.readOnlyFields.firstName = !!this.currentContact.first_name;
      this.readOnlyFields.lastName = !!this.currentContact.last_name;
      this.readOnlyFields.phone = !!this.currentContact.mobile_phone;
      this.readOnlyFields.email = !!this.currentContact.email;
    },
    createTransactionDeskTransaction() {
      this.$refs.form.validate();
      if (!this.hasAllRequiredInformation()) return;

      const payload = {
        contact_id: this.currentContact.id,
        transaction: this.transaction,
        transaction_contact: this.transactionContact,
      };
      this.createTransaction(payload);
    },
    hasAllRequiredInformation() {
      return (
        this.transaction.name &&
        this.transaction.TypeId &&
        this.transactionContact.firstName &&
        this.transactionContact.lastName &&
        this.transactionContact.typeId
      );
    },
    refreshContactEventTimeline() {
      this.$emit('created');
    },
    openTransactionDeskURL() {
      window.open(this.transactionData.data.description);
    },
    setDefaultFormValues() {
      this.transaction.TypeId = null;
      this.transactionContact.typeId = null;
      this.transaction.name = this.getTransactionDefaultName();
      this.transactionContact.firstName = this.currentContact.first_name;
      this.transactionContact.lastName = this.currentContact.last_name;
      this.transactionContact.phone = this.currentContact.mobile_phone;
      this.transactionContact.email = this.currentContact.email;
      this.setIfShouldBeReadOnly();
    },
    async fetchAdditionalTransactionTypes() {
      await this.fetchTransactionTypes(this.transactionTypes.length);
    },
    async fetchAdditionalContactTypes() {
      await this.fetchContactTypes(this.contactTypes.length);
    },
    getTransactionDefaultName() {
      if (!this.currentContact.first_name && !this.currentContact.last_name) {
        return '';
      }

      return `${this.currentContact.first_name || ''} ${this.currentContact.last_name || ''}`;      
    },
  },
};
</script>

<style lang="scss" scoped>
.form-container {
  width: 600px;
}
.form-input {
  max-width: 285px;
}
.bottom-text {
  color: var(--v-secondary-lighten3);
}
</style>
